import { Button, Flex, IconArrowStartLine, Spinner } from "@instructure/ui";
import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Files } from "../../services/FilesService";
import { ReviewService } from "../../services/ReviewService";
import { ShareCodeService } from "../../services/ShareCodeService";
import { fileWriter } from "../../utils/FileUtils";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo/DialogYesNo";
import { CourseReviewContext } from "./CourseReview";

const CourseReviewFooter = ({ setReviews }) => {
  const showNotification = useNotification();
  const courseReviewData = useContext(CourseReviewContext);
  const location = useLocation();
  const { section, cycle, review } = location.state;
  const navigate = useNavigate();

  const [confirmGoBack, setConfirmGoBack] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const [previousPath, setPreviousPath] = useState("");
  const [nextPath, setNextPath] = useState("");

  const showPrevious =
    location.pathname !== "/course-review/home" &&
    location.pathname !== "/course-review/data-view";

  useEffect(() => {
    const path = location.pathname;
    if (path === "/course-review/home") {
      setPreviousPath("");
      setNextPath("");
    } else if (path === "/course-review/data-view") {
      setPreviousPath("");
      setNextPath("/course-review/reflections");
    } else if (path === "/course-review/reflections") {
      setPreviousPath("/course-review/data-view");
      setNextPath("/course-review/actions");
    } else if (path === "/course-review/actions") {
      setPreviousPath("/course-review/reflections");
      setNextPath("/course-review/summary");
    } else if (path === "/course-review/summary") {
      setPreviousPath("/course-review/actions");
      setNextPath("");
    }
  }, [location]);

  async function saveDraft() {
    const draftData = JSON.stringify({
      reflections: courseReviewData.reflections,
      actions: courseReviewData.actions,
      actionsSummary: courseReviewData.draftData.actionsSummary,
      reflectionsSummary: courseReviewData.draftData.reflectionsSummary,
      selectedActions: courseReviewData.draftData.selectedActions,
      selectedReflections: courseReviewData.draftData.selectedReflections,
      results: courseReviewData.draftData.results,
    });

    const jsonFile = fileWriter(draftData, "response.json", "application/json");
    await Files.saveFile(
      jsonFile,
      "reviewDrafts", // only draft review could be saved
      ReviewService.getReviewFolderPath(
        review.cycle.ddl_id,
        review.reviewable.ddl_id,
        review.id
      )
    );
  }

  async function upsertCourseReview() {
    await ReviewService.upsertReview(review); // make sure review node exists
    if (!review) {
      throw new Error("Failed to save course review data");
    }
    setReviews((prevReviews) =>
      prevReviews.map((r) =>
        r.id === review.id ? { ...r, upcoming: false } : r
      )
    );
  }

  async function handleSave() {
    try {
      setSaving(true);
      await saveDraft();
      await upsertCourseReview();
      showNotification(
        t("notifications.general.saveSuccess", {
          obj: t("courseReview.courseReview"),
        })
      );
    } catch {
      showNotification(
        t("notifications.general.saveError", {
          obj: t("courseReview.courseReview"),
        }),
        ERROR
      );
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    try {
      setPublishing(true);
      await saveDraft();
      await upsertCourseReview();

      const shareData = {
        shared_path: "/public/reviews",
        context: {
          review_id: review.id,
          reviewable_id: review.reviewable.ddl_id,
          reviewable_name: review.reviewable.name,
          review_cycle_id: review.cycle.ddl_id,
          academic_years: review.academic_years,
        },
        external_user_ids: [],
      };

      const shareable = await ShareCodeService.createShareUrl(shareData);
      setReviews((prevReviews) =>
        prevReviews.map((r) =>
          r.id === review.id ? { ...r, shareable_code: shareable.code } : r
        )
      );
      review.shareable_code = shareable.code;
      setConfirmPublish(false);
      showNotification(t("courseReview.publishSuccess"));
    } catch (e) {
      if (e.message === "Failed to publish course review data") {
        showNotification(t("courseReview.publishError"), ERROR);
      } else {
        showNotification(t("courseReview.publishError"), ERROR);
      }
    } finally {
      setPublishing(false);
    }
  }

  return (
    <Flex gap="small" wrap="wrap" margin="medium 0 0 0">
      <Flex.Item width="100%">
        <div style={{ borderBottom: "1px solid #ccc" }} />
      </Flex.Item>
      <Flex.Item shouldGrow={location.pathname === "/course-review/data-view"}>
        <Button
          onClick={() => {
            if (location.pathname === "/course-review/home") {
              navigate(`/courses?courseId=${section.canvas_id}`);
            } else if (review.shareable_code) {
              sessionStorage.clear();
              navigate(`/course-review/home`, {
                state: { section: section, cycle: cycle },
              });
            } else {
              setConfirmGoBack(true);
            }
          }}
          renderIcon={IconArrowStartLine}
        >
          {t("global.goBack")}
        </Button>
        <DialogYesNo
          message={t("courseReview.goBackConfirm")}
          openStatus={confirmGoBack}
          onClickNo={() => setConfirmGoBack(false)}
          onClickYes={() => {
            setConfirmGoBack(false);
            sessionStorage.clear();
            navigate(`/course-review/home`, {
              state: { section: section, cycle: cycle },
            });
          }}
        />
      </Flex.Item>
      {showPrevious && !review.shareable_code && (
        <Flex.Item shouldGrow>
          <Button
            onClick={() =>
              navigate(previousPath, {
                state: {
                  section: section,
                  cycle: cycle,
                  review: review,
                  relevantTerms: location.state.relevantTerms,
                },
              })
            }
          >
            {t("global.previous")}
          </Button>
        </Flex.Item>
      )}
      {location.pathname !== "/course-review/home" &&
        !review.shareable_code && (
          <Flex.Item>
            <Button
              onClick={handleSave}
              interaction={saving ? "disabled" : "enabled"}
            >
              {saving ? (
                <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
              ) : (
                t("global.saveDraft")
              )}
            </Button>
          </Flex.Item>
        )}
      {location.pathname !== "/course-review/home" &&
        !review.shareable_code && (
          <Flex.Item>
            <Button
              color="primary"
              onClick={() => {
                if (location.pathname === "/course-review/summary") {
                  setConfirmPublish(true);
                } else {
                  navigate(nextPath, {
                    state: {
                      section: section,
                      cycle: cycle,
                      review: review,
                      relevantTerms: location.state.relevantTerms,
                    },
                  });
                }
              }}
              interaction={publishing ? "disabled" : "enabled"}
            >
              {location.pathname === "/course-review/summary"
                ? t("reflections.publish")
                : t("global.continue")}
            </Button>
            <DialogYesNo
              message={t("courseReview.publishConfirm")}
              openStatus={confirmPublish}
              onClickNo={() => setConfirmPublish(false)}
              onClickYes={handlePublish}
            />
          </Flex.Item>
        )}
    </Flex>
  );
};

export default CourseReviewFooter;
